import { useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { pose } from "./Player";
import { WALL } from "./BenchWall";
import { BENCH_Z, ROOM } from "../../lib/layout";

export const cine = { mode: null, t: 0, from: null, cam: {} };
const SEAT = { x: 0, y: 1.12, z: 0.18 };
export const STAND = { x: 0, z: BENCH_Z - 3.4, yaw: Math.PI, pitch: -0.04 };
const WATCH = { x: 0, z: BENCH_Z - 3.6, yaw: Math.PI, pitch: -0.07 };
const TAU = Math.PI * 2;
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const lerp = (a, b, k) => a + (b - a) * k;
const lerpAngle = (a, b, k) => {
  const d = ((((b - a) % TAU) + TAU + Math.PI) % TAU) - Math.PI;
  return a + d * k;
};

export function SeatCamera({ seated, revealing }) {
  const { camera, gl } = useThree();
  const was = useRef(false);
  const res = useRef({ base: 1, v: 1, n: 0, t: 0 });
  useFrame((_, delta) => {
    const r = res.current;
    if (revealing && !seated && cine.mode !== "watch") {
      cine.mode = "watch";
      cine.t = 0;
      cine.from = { x: camera.position.x, y: camera.position.y, z: camera.position.z, yaw: camera.rotation.y, pitch: camera.rotation.x };
    }
    if (seated !== was.current) {
      was.current = seated;
      if (seated) Object.assign(r, { base: gl.getPixelRatio(), v: gl.getPixelRatio(), n: 0, t: 0 });
      else {
        gl.setPixelRatio(r.base);
        Object.assign(pose, STAND);
      }
      cine.mode = seated ? "sit" : "stand";
      cine.t = 0;
      cine.from = { x: camera.position.x, y: camera.position.y, z: camera.position.z, yaw: camera.rotation.y, pitch: camera.rotation.x };
    }
    if (seated) {
      r.n++;
      r.t += delta;
      if (r.t > 1.2) {
        if (r.n / r.t < 32 && r.v > 0.5) {
          r.v = Math.max(0.5, +(r.v - 0.15).toFixed(2));
          gl.setPixelRatio(r.v);
        }
        cine.fps = Math.round(r.n / r.t);
        r.n = 0;
        r.t = 0;
      }
      cine.dpr = r.v;
    }
    if (cine.mode) {
      cine.t += cine.mode === "watch" ? delta : Math.min(delta, 0.1);
      const f = cine.from;
      if (cine.mode === "watch") {
        const k = ease(Math.min(1, cine.t / 2.2));
        camera.position.set(lerp(f.x, WATCH.x, k), lerp(f.y, ROOM.EYE, k), lerp(f.z, WATCH.z, k));
        camera.rotation.set(lerp(f.pitch, WATCH.pitch, k), lerpAngle(f.yaw, WATCH.yaw, k), 0);
      } else if (cine.mode === "sit") {
        const k = ease(Math.min(1, cine.t / 4.5));
        const drift = Math.min(1, Math.max(0, cine.t - 4.5) / 60);
        const sway = Math.sin(cine.t * 0.21) * 0.04 * Math.min(1, cine.t / 6);
        const pitch = Math.atan2(WALL.y - SEAT.y, SEAT.z - WALL.z);
        camera.position.set(lerp(f.x, -(SEAT.x + sway), k), lerp(f.y, SEAT.y, k), lerp(f.z, BENCH_Z - SEAT.z + drift * 0.35, k));
        camera.rotation.set(lerp(f.pitch, pitch, k), lerpAngle(f.yaw, Math.PI + sway * 0.15, k), 0);
      } else {
        const k = ease(Math.min(1, cine.t / 1.8));
        camera.position.set(lerp(f.x, STAND.x, k), lerp(f.y, ROOM.EYE, k), lerp(f.z, STAND.z, k));
        camera.rotation.set(lerp(f.pitch, STAND.pitch, k), lerpAngle(f.yaw, STAND.yaw, k), 0);
        if (k >= 1) {
          Object.assign(pose, STAND);
          cine.mode = null;
        }
      }
    }
    const p = camera.position;
    const r2 = (v, n = 2) => (Number.isFinite(v) ? +v.toFixed(n) : null);
    cine.cam = { x: r2(p.x), y: r2(p.y), z: r2(p.z), pitch: r2(camera.rotation.x, 3), yaw: r2(camera.rotation.y, 3) };
  });
  return null;
}
